import { ref } from 'vue'

/**
 * Optix Panel Integration
 * Sends AdminSidePanelPush to the parent Optix app to open a side panel
 * with our content loaded inside it.
 *
 * Reference format from floor-plan-form app:
 * AdminSidePanelPush {
 *   client_id: '12075',
 *   canvasUUID: 'floor-plan-form',
 *   url: 'https://floorplan.optixapp.io/?organization_id=120…&panel=new-floor-plan'
 * }
 */

const DASHBOARD_CANVAS_ID = 'dashboard'

/**
 * Get client_id and canvasUUID from URL (same pattern as other Optix widgets)
 */
export function getOptixParams() {
  const urlParams = new URLSearchParams(window.location.search)
  let clientId = urlParams.get('client_id') || urlParams.get('clientId') || ''
  let canvasUUID = urlParams.get('canvasUUID') || urlParams.get('canvas_uuid') || urlParams.get('canvasUuid') || ''

  if (document.referrer) {
    try {
      const referrerParams = new URLSearchParams(new URL(document.referrer).search)
      if (!clientId) clientId = referrerParams.get('client_id') || referrerParams.get('clientId') || ''
      if (!canvasUUID) canvasUUID = referrerParams.get('canvasUUID') || referrerParams.get('canvas_uuid') || referrerParams.get('canvasUuid') || ''
    } catch (_e) {}
  }

  if (!clientId || !canvasUUID) {
    try {
      if (window.parent && window.parent !== window) {
        const parentParams = new URLSearchParams(new URL(window.parent.location.href).search)
        if (!clientId) clientId = parentParams.get('client_id') || parentParams.get('clientId') || ''
        if (!canvasUUID) canvasUUID = parentParams.get('canvasUUID') || parentParams.get('canvas_uuid') || parentParams.get('canvasUuid') || ''
      }
    } catch (_e) {}
  }

  return {
    client_id: clientId || undefined,
    canvasUUID: canvasUUID || DASHBOARD_CANVAS_ID
  }
}

/**
 * Build the panel URL with current params + panel=add-widget and any extra query params.
 * @param {string} panelType - Panel identifier (e.g. 'add-widget')
 * @param {Record<string, string>} [extraParams] - Optional query params to pass to the panel (e.g. { sectionId: 'section-123' })
 */
function buildPanelUrl(panelType = 'add-widget', extraParams = {}) {
  const url = new URL(window.location.href)
  url.searchParams.set('panel', panelType)
  for (const [key, value] of Object.entries(extraParams)) {
    if (value != null && value !== '') {
      url.searchParams.set(key, value)
    }
  }
  return url.toString()
}

/**
 * Send AdminSidePanelPush to parent Optix app.
 * The parent will open its side panel and load our URL inside it.
 *
 * @param {string} panelType - Panel identifier (e.g. 'add-widget')
 * @param {Record<string, string>} [panelQueryParams] - Optional query params to pass to the panel (e.g. { sectionId: 'section-123' })
 * @returns {boolean} - True if message was sent
 */
export function pushAdminSidePanel(panelType = 'add-widget', panelQueryParams = {}) {
  // In a normal iframe, window.parent !== window. On the Optix mobile app
  // (WKWebView) they may be equal — still try to postMessage so the native
  // bridge can intercept it.
  const inIframe = window.parent && window.parent !== window
  if (!inIframe && !hasOptixUrlParams()) {
    return false
  }

  const params = getOptixParams()
  const panelUrl = buildPanelUrl(panelType, panelQueryParams)

  const VIEW_ALL_PANELS = ['view-all-actions', 'view-all-time-saved', 'view-all-top-bookers', 'view-all-revenue-collected', 'view-all-conversion-accounts', 'view-all-support-chat', 'view-all-groups']
  const maxWidth = panelType === 'view-all-best-practices' ? '600px' : panelType === 'widget-settings' ? '500px' : panelType === 'dashboard-settings' ? '500px' : panelType === 'widget-detail' ? '580px' : panelType === 'inbox-conversation' ? '500px' : panelType === 'discover-groups' ? '400px' : VIEW_ALL_PANELS.includes(panelType) ? '520px' : '1000px'
  const message = {
    client_id: params.client_id,
    canvasUUID: params.canvasUUID,
    url: panelUrl,
    options: {
      'max-width': maxWidth
    }
  }

  try {
    // Post to parent (iframe) or self (WKWebView — native bridge listens on window)
    const target = inIframe ? window.parent : window
    target.postMessage(message, '*')
    // Also try command structure (some apps use wrapped format)
    target.postMessage(
      { command: 'AdminSidePanelPush', payload: message },
      '*'
    )
    target.postMessage(
      { command: 'AdminSidePanelPush', ...message },
      '*'
    )
    if (inIframe && window.top && window.top !== window.parent) {
      window.top.postMessage(message, '*')
    }
    return true
  } catch (error) {
    console.error('Failed to send AdminSidePanelPush:', error)
    return false
  }
}

/**
 * Open a conversation in the Optix conversation panel.
 * Sends OpenMessageInConversationPanel to the parent Optix app - this is how Optix natively
 * opens the conversation panel (e.g. from users list, inbox, etc.).
 *
 * @param {string} conversationId - The conversation ID to open (e.g. '136791-153051')
 * @param {string} [accountId] - Optional account ID (other participant in direct message)
 * @returns {boolean} - True if message was sent
 */
export function openMessageInConversationPanel(conversationId, accountId) {
  if (!conversationId) return false
  const inIframe = window.parent && window.parent !== window
  if (!inIframe && !hasOptixUrlParams()) return false

  const params = getOptixParams()
  const payload = {
    client_id: params.client_id,
    canvasUUID: params.canvasUUID,
    conversation_id: conversationId
  }
  if (accountId) payload.account_id = accountId

  try {
    const target = inIframe ? window.parent : window
    target.postMessage({ command: 'OpenMessageInConversationPanel', payload }, '*')
    target.postMessage({ command: 'OpenMessageInConversationPanel', ...payload }, '*')
    if (inIframe && window.top && window.top !== window.parent) {
      window.top.postMessage({ command: 'OpenMessageInConversationPanel', payload }, '*')
      window.top.postMessage({ command: 'OpenMessageInConversationPanel', ...payload }, '*')
    }
    return true
  } catch (error) {
    console.error('Failed to send OpenMessageInConversationPanel:', error)
    return false
  }
}

/**
 * Open the inbox conversation panel with a specific conversation.
 * Uses OpenMessageInConversationPanel (Optix native) when in iframe; falls back to
 * AdminSidePanelPush for dashboard-hosted panel.
 *
 * @param {string} conversationId - The conversation ID to open
 * @param {string} [accountId] - Optional account ID (other participant in direct message)
 * @returns {boolean} - True if message was sent
 */
export function pushConversationPanel(conversationId, accountId) {
  if (!conversationId) return false
  // Prefer Optix native command when embedded (iframe or WKWebView)
  if (isInOptixIframe() && openMessageInConversationPanel(conversationId, accountId)) {
    return true
  }
  // Fallback: dashboard-hosted panel with iframe
  const extraParams = { conversation: conversationId }
  if (document.referrer) {
    try {
      const ref = new URL(document.referrer)
      if (ref.origin !== window.location.origin) {
        extraParams.optix_admin_origin = ref.origin
      }
    } catch (_e) {}
  }
  return pushAdminSidePanel('inbox-conversation', extraParams)
}

/**
 * Send AdminSidePanelPop to parent Optix app to close the side panel.
 *
 * Reference format from floor-plan-form app:
 * AdminSidePanelPop { client_id: '12075', canvasUUID: 'floor-plan-form' }
 *
 * @returns {boolean} - True if message was sent
 */
export function popAdminSidePanel() {
  const inIframe = window.parent && window.parent !== window
  if (!inIframe && !hasOptixUrlParams()) {
    return false
  }

  const params = getOptixParams()
  const message = {
    client_id: params.client_id,
    canvasUUID: params.canvasUUID
  }

  try {
    const target = inIframe ? window.parent : window
    target.postMessage(message, '*')
    target.postMessage({ command: 'AdminSidePanelPop', payload: message }, '*')
    if (inIframe && window.top && window.top !== window.parent) {
      window.top.postMessage(message, '*')
    }
    return true
  } catch (error) {
    console.error('Failed to send AdminSidePanelPop:', error)
    return false
  }
}

/**
 * Send GetParentSize to parent Optix app.
 * OCC responds with ParentSize broadcast; parent may use this to size the panel.
 * Call this when the panel content has loaded (e.g. panel mode).
 *
 * Format from Optix logs: GetParentSize { client_id: undefined, canvasUUID: '...' }
 */
export function requestParentSize() {
  if (!window.parent || window.parent === window) {
    return false
  }

  const params = getOptixParams()
  const message = {
    client_id: params.client_id,
    canvasUUID: params.canvasUUID
  }

  try {
    window.parent.postMessage(message, '*')
    window.parent.postMessage({ command: 'GetParentSize', ...message }, '*')
    window.parent.postMessage({ command: 'GetParentSize', payload: message }, '*')
    if (window.top && window.top !== window.parent) {
      window.top.postMessage(message, '*')
      window.top.postMessage({ command: 'GetParentSize', payload: message }, '*')
    }
    return true
  } catch (error) {
    console.error('Failed to send GetParentSize:', error)
    return false
  }
}

/**
 * Send ToggleOptixMenu to parent Optix app.
 * Toggles the Optix sidebar/menu (e.g. for mobile burger menu).
 *
 * Format from Optix logs: ToggleOptixMenu { client_id: undefined, canvasUUID: '...' }
 */
export function toggleOptixMenu() {
  if (!window.parent || window.parent === window) {
    return false
  }

  const params = getOptixParams()
  const payload = {
    client_id: params.client_id,
    canvasUUID: params.canvasUUID
  }

  try {
    window.parent.postMessage(payload, '*')
    window.parent.postMessage({ command: 'ToggleOptixMenu', payload }, '*')
    window.parent.postMessage({ command: 'ToggleOptixMenu', ...payload }, '*')
    if (window.top && window.top !== window.parent) {
      window.top.postMessage(payload, '*')
      window.top.postMessage({ command: 'ToggleOptixMenu', payload }, '*')
    }
    return true
  } catch (error) {
    console.error('Failed to send ToggleOptixMenu:', error)
    return false
  }
}

/**
 * Check if we're in an Optix context via URL parameters.
 * On the Optix mobile app (iOS WKWebView), the dashboard may not be in a
 * traditional iframe, but Optix passes params like optix-mobile, client_id, etc.
 */
function hasOptixUrlParams() {
  try {
    const params = new URLSearchParams(window.location.search)
    return !!(
      params.get('optix-mobile') ||
      params.get('optix_mobile') ||
      params.get('client_id') ||
      params.get('clientId')
    )
  } catch {
    return false
  }
}

/**
 * Check if we're in an Optix iframe (can use parent panel).
 * Also returns true when Optix URL parameters are present (e.g. mobile app
 * WKWebView where window.self === window.top but we're still embedded).
 */
export function isInOptixIframe() {
  try {
    if (window.self !== window.top) return true
  } catch {
    return true
  }
  // Fallback: detect Optix mobile context via URL params
  return hasOptixUrlParams()
}

/**
 * Get the correct postMessage target for communicating with Optix.
 * In a normal iframe, posts to window.parent. On WKWebView (mobile app),
 * window.parent === window — post to window so the native bridge can intercept.
 *
 * @returns {{ target: Window, inIframe: boolean }} - target to postMessage to, and whether we're in a real iframe
 */
export function getPostMessageTarget() {
  const inIframe = window.parent && window.parent !== window
  return {
    target: inIframe ? window.parent : window,
    inIframe
  }
}

/**
 * Debug: enable via ?debug_optix=1 or localStorage.setItem('debug_optix', '1')
 * Logs all postMessage events from parent so we can discover menu state format.
 */
function isOptixDebugEnabled() {
  if (typeof window === 'undefined') return false
  try {
    return (
      new URLSearchParams(window.location.search).get('debug_optix') === '1' ||
      localStorage.getItem('debug_optix') === '1'
    )
  } catch {
    return false
  }
}

let optixMessageListenerAttached = false

/**
 * Listen for Optix parent broadcasts (ParentSize, ToggleOptixMenu, etc.)
 * and infer whether the Optix menu/sidebar is visible.
 *
 * Native Optix pages only show the burger icon when the menu is NOT visible.
 * We try to detect this from:
 * - ParentSize.sizes (may include menuVisible, sidebarWidth, etc.)
 * - Any OptixMenuState or similar command
 *
 * Returns { showBurgerIcon, attachListener }
 * showBurgerIcon: true = show our burger (menu is hidden, user needs a way to open it)
 */
export function useOptixMenuState() {
  const showBurgerIcon = ref(true) // default: show (we don't have menu state yet)

  function attachListener() {
    if (optixMessageListenerAttached || typeof window === 'undefined') return
    optixMessageListenerAttached = true

    window.addEventListener('message', (event) => {
      const d = event.data
      if (!d || typeof d !== 'object') return

      const cmd = d.command
      const payload = d.payload ?? d
      const sizes = payload?.sizes ?? d.sizes

      // Debug: log ALL messages from parent to discover menu state format.
      // Enable with ?debug_optix=1 or localStorage.setItem('debug_optix','1')
      if (isOptixDebugEnabled()) {
      }

      // ParentSize: OCC broadcasts { command: 'ParentSize', sizes: { sidePanelOpen, parentWidth, parentHeight, mobileSize } }
      if (cmd === 'ParentSize' && sizes) {
        const sidePanelOpen = sizes.sidePanelOpen
        if (typeof sidePanelOpen === 'boolean') {
          showBurgerIcon.value = !sidePanelOpen
        }
        if (isOptixDebugEnabled()) {
        }
      }
    })
  }

  return { showBurgerIcon, attachListener }
}
